import React, { useEffect, useState } from "react";
import {
  Box,
  CircularProgress,
  Typography,
  Stack,
  Paper,
  Alert,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { apiConfig } from "../../config/api.config";
import { decodeJWTToken } from "../../contexts/AuthProvider/AuthProvider.utils";
import { SuccessDialog } from "../../components/Dialogs/SuccessDialog";
import { loadRazorpay } from "../../utils/loadRazorpay";

interface ISharePayload {
  token: string;
  payload: {
    req_plan_id: string;
    customer_requested_status: string;
    addons: string[];
    plan_name: string;
    company_outlet_uuid: string;
  };
}

export const PublicSubscriptionPurchase: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const [status, setStatus] = useState<
    "loading" | "success" | "error" | "invalid"
  >("loading");
  const [errorMessage, setErrorMessage] = useState("");
  const [openSuccess, setOpenSuccess] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const data = params.get("data");
    console.log("PublicSubscriptionPurchase Entry ", data)
    if (!data) {
      console.log("PublicSubscriptionPurchase Invalid 1", data)
      setStatus("invalid");
      return;
    }

    let decoded: ISharePayload;
    try {
      decoded = JSON.parse(atob(decodeURIComponent(data)));
      console.log("PublicSubscriptionPurchase Success ", decoded)
    } catch {
      console.log("PublicSubscriptionPurchase Invalid 2", data)
      setStatus("invalid");
      return;
    }

    const { token, payload } = decoded;

    if (!token || !payload?.req_plan_id || !payload?.company_outlet_uuid) {
      console.log("PublicSubscriptionPurchase Invalid 3", decoded)
      setStatus("invalid");
      return;
    }

    const { req_plan_id, customer_requested_status, addons, plan_name, company_outlet_uuid } = payload;

    // Decode JWT token to get user info for Razorpay prefill
    const tokenData = decodeJWTToken(token);
    const companyLogo = tokenData?.LOGO
      ? `${process.env.REACT_APP_S3_BASE_URL || ""}/${tokenData.LOGO}`
      : "/logo.png";

    const executePurchase = async () => {
      try {
        const baseUrl = `${apiConfig.baseUrl}`

        const response = await axios.post(
          `${baseUrl}/subscription/upsert-company-outlet-subscription-public-url`,
          {
            company_outlet_uuid: company_outlet_uuid,
            req_plan_id: req_plan_id,
            customer_requested_status:
              customer_requested_status || "SUBSCRIPTION_RQ",
            addons: addons || [],
          },
          {
            headers: { "auth-Token": token },
          },
        );

        if (response?.data?.data) {
          const gateway_subscription_id =
            response.data.data.gateway_subscription_id;
          const resporzp_key = response.data.data.rzp_key;
          const rzp_type = response.data.data.rzp_type;
          const rzp_order = response.data.data.rzp_order;

          const options = {
            key: resporzp_key || "",
            ...(rzp_type === "subscription") ? { subscription_id: gateway_subscription_id }
              : { order_id: rzp_order?.id },
            name: plan_name || "Subscription Plan",
            description: "Subscription purchase",
            image: companyLogo,
            recurring: rzp_type === "subscription",
            handler: function (_response: any) {
              setStatus("success");
              setOpenSuccess(true);
            },
            prefill: {
              name: tokenData?.full_name || "",
              email: tokenData?.email || "",
              contact: "",
            },
            theme: {
              color: "#0074d4",
            },
          };
          //@ts-ignore
          const loaded = await loadRazorpay();
          if (!loaded) {
            setStatus("error");
            return;
          }
          //@ts-ignore
          const rzp = new window.Razorpay(options);
          rzp.on("payment.failed", function (response: any) {
            setStatus("error");
            setErrorMessage(
              response?.error?.description || "Payment failed",
            );
          });
          rzp.open();
        } else {
          setStatus("success");
          setOpenSuccess(true);
        }
      } catch (err: any) {
        setStatus("error");
        setErrorMessage(
          err?.response?.data?.message || "Failed to process subscription",
        );
      }
    };

    executePurchase();
  }, [location.search]);

  if (status === "invalid") {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 3,
        }}
      >
        <Paper sx={{ p: 4, maxWidth: 500, textAlign: "center", borderRadius: 3 }}>
          <Typography variant="h4" fontWeight={700} gutterBottom>
            Invalid Link
          </Typography>
          <Typography color="text.secondary">
            This subscription link is invalid or has expired. Please request a
            new link.
          </Typography>
        </Paper>
      </Box>
    );
  }

  if (status === "error") {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 3,
        }}
      >
        <Paper sx={{ p: 4, maxWidth: 500, textAlign: "center", borderRadius: 3 }}>
          <Typography variant="h4" fontWeight={700} gutterBottom color="error">
            Purchase Failed
          </Typography>
          <Alert severity="error" sx={{ mt: 2 }}>
            {errorMessage}
          </Alert>
        </Paper>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 3,
      }}
    >
      <Paper sx={{ p: 4, maxWidth: 500, textAlign: "center", borderRadius: 3 }}>
        <Stack spacing={3} alignItems="center">
          <CircularProgress size={48} />
          <Typography variant="h4" fontWeight={700}>
            Processing Subscription
          </Typography>
          <Typography color="text.secondary">
            Please wait while we set up your payment...
          </Typography>
        </Stack>
      </Paper>

      {openSuccess && (
        <SuccessDialog
          content="You have successfully subscribed to the plan."
          open={openSuccess}
          onClose={() => {
            setOpenSuccess(false);
            navigate("/");
          }}
        />
      )}
    </Box>
  );
};
